/* eslint-disable @next/next/no-img-element */
import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import Tippy from "@tippyjs/react";

import "tippy.js/dist/tippy.css";

import styles from "../../styles/tinymce.module.css";

import { getLocalStorage, setLocalStorage } from "../../helpers/storage";
import {
  generateMotionWording,
  getGroupForTitleEngage,
  getItemNumber,
  getGroupForTitle,
  tarrantInlineText,
} from "../../helpers/text";
import { engageState } from "../../atoms/platform";
import {
  getNameByGroupId,
  getMinutesInfoByGroupId,
} from "../../helpers/arrays";
import { apiGetCachedMinutes } from "../../lib/api/engage";
import { apiGetPlatformOptions } from "../../lib/api/platform";
import { textToHtml, textToHtmlTinyMCE } from "../../helpers/converter";

import { XIcon, PrinterIcon, DownloadIcon } from "@heroicons/react/outline";
import { MdOndemandVideo } from "react-icons/md";
import { IoChevronBack } from "react-icons/io5";
import { AiOutlinePlayCircle } from "react-icons/ai";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";
import { IoMdOpen } from "react-icons/io";

import AgendaTopicFull from "./AgendaTopicFull";
import MinutesTopicInfoMobile from "./MinutesTopicInfoMobile";
// import EngageHeader from "../Builders/Header/EngageHeader";
import Loading from "../Shared/Loading";
import Supplemental from "./Supplemental";
import SupplementalMobile from "./SupplementalMobile";
import MinutesRenderEngage from "../TinyMce/MinutesrRenderEngage";
import EditorHTML from "../RichText/EditorHTML";
import EditorHTMLSmall from "../RichText/EditorHTMLSmall";

function isStringifiedObject(str) {
  if (typeof str !== "string") return false;

  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === "object";
  } catch {
    return false;
  }
}

const Minutes = () => {
  const router = useRouter();
  const { client, minutes, cablecast_embed } = router.query;
  const [showDetails, setShowDetails] = useState(false);
  const [mobileDetails, setMobileDetails] = useState(false);
  const [topicDetails, setTopicDetails] = useState();
  const [platform, setPlatform] = useRecoilState(engageState);
  const [data, setData] = useState();
  const [isInIframe, setIsInIframe] = useState(false);
  const [supplemental, setSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const [mobileSupplemental, setMobileSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const [loading, setLoading] = useState(false);
  const [groupOrder, setGroupOrder] = useState();

  useEffect(() => {
    async function fetchOptions() {
      const result = await apiGetPlatformOptions(client);
      if (result) {
        setPlatform(result);
        setLocalStorage("platform", result);
      }
    }
    if (cablecast_embed === "true") {
      const localStorage = getLocalStorage("platform");
      if (!platform) {
        if (!localStorage) {
          fetchOptions();
        }
      } else {
        if (platform?.onboarded) {
          if (!localStorage) {
            fetchOptions();
          } else {
            setPlatform(localStorage);
          }
        } else {
          fetchOptions();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cablecast_embed]);

  useEffect(() => {
    async function fetchMinutes() {
      setLoading(true);
      const result = await apiGetCachedMinutes(minutes);
      console.log(result);
      if (result) {
        setData(result);
        setGroupOrder(result.groupOrder);
      }
      setLoading(false);
    }
    if (minutes) {
      fetchMinutes();
      if (window.self !== window.top) {
        setIsInIframe(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutes]);

  function handleChosenTopic(topic) {
    setTopicDetails(topic);
    setShowDetails(true);
  }

  function handleVideoJump(time) {
    if (window && window.parent) {
      window.parent.postMessage(
        {
          message: "seek",
          seconds: time,
        },
        "*",
      );
    }
  }

  function handleMobileTopic(topic) {
    setTopicDetails(topic);
    setMobileDetails(true);
  }

  return (
    <div className="pb-sm antialiased w-full 2xl:max-w-7xl 2xl:mx-auto">
      {/* Desktop Header */}
      {data && (
        <div
          className={clsx(
            showDetails ? "z-0" : "z-10",
            "hidden sm:block sticky top-0 max-w-7xl 2xl:max-w-7/8 mx-auto px-4 sm:px-6 md:px-8 pt-6 bg-white",
          )}
        >
          <div className="pb-2 border-b border-gray-400 flex flex-col items-end mt-2">
            {/* <div className="col-span-2 flex flex-col items-end lg:items-center justify-end">
           
            </div> */}
            <div className="col-span-4 lg:col-span-1 mt-4 lg:mt-0 flex justify-end items-end space-x-2">
              <div className="flex items-center space-x-2 mr-2">
                {platform?.allowSupplemental &&
                  data.attachments?.length > 0 && (
                    <Tippy
                      content="Supplemental Materials"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div
                        className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        onClick={() =>
                          setSupplemental({
                            visible: true,
                            meeting: data,
                          })
                        }
                      >
                        <HiOutlineDocumentDuplicate className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </div>
                    </Tippy>
                  )}

                {data?.videoUrl &&
                  data.publishStatus !== "published" &&
                  !cablecast_embed && (
                    <Tippy
                      content="Watch Video of the Meeting"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div>
                        <a
                          href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                          title="Watch Video"
                          role="button"
                          aria-label="Open Webpage with Video"
                        >
                          <MdOndemandVideo className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                        </a>
                      </div>
                    </Tippy>
                  )}
                {data?.minutesPdfUrl && (
                  <Tippy
                    content="Download Minutes"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={data.minutesPdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm md:text-base flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-1.5 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200focus:outline-none"
                      >
                        <DownloadIcon className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                        Minutes
                      </a>
                    </div>
                  </Tippy>
                )}
                {data?.agendaPacketUrl && (
                  <Tippy
                    content="Download Agenda Packet"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={data.agendaPacketUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm md:text-base flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-1.5 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200focus:outline-none"
                        title="Download Agenda Packt"
                        role="button"
                        aria-label="Download Agenda Packet"
                      >
                        <DownloadIcon className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1 mr-1" />
                        Packet
                      </a>
                    </div>
                  </Tippy>
                )}
              </div>
              {isInIframe && (
                <Tippy
                  content="Open Agenda in New Window"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <a
                    href={router.asPath}
                    target="_blank"
                    rel="noreferrer"
                    title="Open Agenda in New Window"
                    role="button"
                    aria-label="Open Agenda in new window"
                  >
                    <IoMdOpen className="h-5 w-5 md:h-6 md:w-6 hover:text-blue-500 transition duration-200" />
                  </a>
                </Tippy>
              )}
              {!cablecast_embed && (
                <XIcon
                  className="h-5 w-5 md:h-6 md:w-6 xl:h-8 xl:w-8 2xl:h-10 2xl:w-10 text-gray-800 cursor-pointer hover:text-delete-btn transform duration-150 hover:rotate-180"
                  aria-hidden="true"
                  onClick={() => {
                    const newPath = `${process.env.NEXT_PUBLIC_BASE_URL}/engage-v2/${client}/agendas`;
                    router.replace(newPath);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile header */}
      {data && (
        <div className="sticky sm:hidden z-10 top-0 px-4 sm:px-6 pt-2 bg-white">
          <div className="pb-1 border-b border-gray-400">
            <div className="relative flex items-center justify-center">
              {!cablecast_embed && (
                <IoChevronBack
                  className="absolute top-1 left-0 h-6 w-6 text-gray-800 cursor-pointer hover:text-gray-400"
                  aria-hidden="true"
                  onClick={() => {
                    const newPath = `${process.env.NEXT_PUBLIC_BASE_URL}/engage-v2/${client}/agendas`;
                    router.replace(newPath);
                  }}
                />
              )}
            </div>
            <div className="mt-2 flex justify-end items-center space-x-2">
              {platform?.allowSupplemental && data.attachments?.length > 0 && (
                <Tippy
                  content="Supplemental Resources"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <div
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      onClick={() => {
                        setMobileSupplemental({
                          visible: true,
                          meeting: data,
                        });
                      }}
                    >
                      <HiOutlineDocumentDuplicate className="h-5 w-5" />
                    </div>
                  </div>
                </Tippy>
              )}
              {data?.videoUrl && data.publishStatus !== "published" && (
                <Tippy
                  content="Watch Video of the Meeting"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      title="Watch Video"
                      role="button"
                      aria-label="Open Webpage with Video"
                    >
                      <MdOndemandVideo className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              <Tippy
                content="Print Agenda"
                placement="bottom"
                animation="scale"
                delay={[500, 0]}
              >
                <div>
                  <a
                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/${client}/print/minutes/${data?.id}?print=true`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                    title="Print Agenda Cover Sheet"
                    role="button"
                    aria-label="Print Agenda Cover Sheet"
                  >
                    <PrinterIcon className="h-5 w-5" />
                  </a>
                </div>
              </Tippy>
              {data?.agendaPdfUrl && (
                <Tippy
                  content="Download Agenda"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={data?.agendaPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      title="Download Agenda Packet"
                      role="button"
                      aria-label="Download Agenda Packet"
                    >
                      <DownloadIcon className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              {data?.packetPdfUrl && (
                <Tippy
                  content="Download Agenda Packet"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={data?.packetPdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      title="Download Agenda Packet"
                      role="button"
                      aria-label="Download Agenda Packet"
                    >
                      <DownloadIcon className="h-5 w-5 mr-1" />
                      Packet
                    </a>
                  </div>
                </Tippy>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agenda Details */}
      {data && (
        <div className="lg:max-w-7xl 2xl:max-w-7/8 mx-auto px-2 lg:px-8">
          {/* Heading Blurbs */}
          <MinutesRenderEngage meeting={data} />

          {/* Start Agenda */}
          <div className="mt-4 2xl:mt-8 hidden lg:block">
            <div className="mt-4">
              {/* List */}
              <div>
                {groupOrder &&
                  groupOrder.map((group, index) => {
                    const { bulkVote } = group;
                    const minutesInfo = getMinutesInfoByGroupId(
                      group._id,
                      data.groupSubheadings,
                    );
                    const name = getNameByGroupId(
                      group._id,
                      data.groupSubheadings,
                      group.name,
                    );
                    const groupActions = data.actionTopics
                      .map((topic) => {
                        if (
                          topic.agendaGroup._id.toString() ===
                          group._id.toString()
                        ) {
                          const motionWithVoteNumber = topic.motions?.find(
                            (motion) => motion.useVoteNumber === true,
                          );

                          let voteNumber =
                            motionWithVoteNumber?.voteNumber ?? "";

                          if (client === "tarrantcountytx" && voteNumber) {
                            voteNumber = `Court Order Number ${voteNumber.substring(
                              1,
                            )}`;
                          }

                          return {
                            ...topic,
                            voteNumber,
                          };
                        }
                        return null;
                      })
                      .filter((topic) => topic !== null);
                    groupActions.sort((a, b) => {
                      if (a.bulkTopic === true) return -1; // Move the entry with bulkTopic: true to the front
                      if (b.bulkTopic === true) return 1; // Move the entry with bulkTopic: true to the front
                      return a.order - b.order; // Sort by the order property for other entries
                    });
                    return (
                      <>
                        {group?._id.toString() ===
                          "682aa91b3c8f84005c5b9f81" && (
                          <div className={`mb-4 ${styles.tinyContainer}`}>
                            {textToHtmlTinyMCE(tarrantInlineText)}
                          </div>
                        )}
                        <div
                          key={`group-${index}`}
                          className={clsx(
                            data?.department.agendaFont === "arial" &&
                              "font-arial",
                            data?.department.agendaFont === "sans-serif" &&
                              "font-sans",
                            data?.department.agendaFont === "timesNewRoman" &&
                              "font-timesNewRoman",
                            data?.department.agendaFont === "noto" &&
                              "font-noto",
                            group?.parentGroup && "ml-6",
                            "mb-3 lg:mb-4 xl:mb-8 text-black",
                          )}
                        >
                          <div
                            className={clsx(
                              data?.department.agendaFont === "arial" &&
                                "font-arial",
                              data?.department.agendaFont === "sans-serif" &&
                                "font-sans",
                              data?.department.agendaFont === "timesNewRoman" &&
                                "font-timesNewRoman",
                              data?.department.agendaFont === "noto" &&
                                "font-noto",
                              data?.coverOptions?.useSeperators &&
                                "border-b border-black",
                            )}
                          >
                            <p
                              className={clsx(
                                data?.department.agendaOptions
                                  .agendaGroupBold && "font-semibold",
                                data?.department.agendaOptions
                                  ?.agendaGroupFontSize &&
                                  `text-${data.department.agendaOptions.agendaGroupFontSize}`,
                              )}
                            >
                              {getGroupForTitleEngage(
                                data?.groupNumbering,
                                index,
                                data?.groupOrder,
                                data?.childNumbering,
                              )}{" "}
                              {name}
                            </p>
                          </div>
                          {minutesInfo?.length > 0 &&
                            isStringifiedObject(minutesInfo) && (
                              <EditorHTML value={minutesInfo} />
                            )}
                          {minutesInfo.length > 0 &&
                            !isStringifiedObject(minutesInfo) && (
                              <div className={`mb-4 ${styles.tinyContainer}`}>
                                {textToHtmlTinyMCE(minutesInfo)}
                              </div>
                            )}
                          {groupActions.map((topic, i) => {
                            if (topic.bulkTopic) {
                              return (
                                <div className="mt-1 mb-3" key={i}>
                                  {topic.minutesInfo.length > 0 &&
                                    topic.minutesEditor === 1 && (
                                      <EditorHTML value={topic.minutesInfo} />
                                    )}
                                  {topic.minutesInfo.length > 0 &&
                                    topic.minutesEditor === 2 && (
                                      <div
                                        className={`mb-4 ${styles.tinyContainer}`}
                                      >
                                        {textToHtmlTinyMCE(topic.minutesInfo)}
                                      </div>
                                    )}
                                  {topic.motions.map((motion, mIdx) => {
                                    let insertedText = "";
                                    if (motion.overWriteDefault) {
                                      insertedText = motion.wording;
                                    } else {
                                      insertedText = generateMotionWording(
                                        data.board.motionText,
                                        motion,
                                        topic,
                                      );
                                    }

                                    return (
                                      <div
                                        className={clsx(
                                          `font-${data?.department.agendaFont}`,
                                          data?.department.agendaOptions
                                            .motionBold && "font-semibold",
                                          data?.department.agendaOptions
                                            ?.motionFontSize &&
                                            `text-${data.department.agendaOptions.motionFontSize}`,
                                          "text-justify whitespace-pre-line",
                                        )}
                                        key={`group-${index}-motion-${mIdx}`}
                                      >
                                        <p>{insertedText}</p>
                                        {!motion.overWriteDefault &&
                                          motion.wording?.length > 0 && (
                                            <p>{motion.wording}</p>
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            } else {
                              let tIdx = i;
                              if (bulkVote) {
                                tIdx--;
                              }
                              return (
                                <div key={`group-${index}-topic-${i}`}>
                                  <Tippy
                                    content="Click to view item details"
                                    placement="top-start"
                                    animation="scale"
                                    delay={[2000, 0]}
                                  >
                                    <div
                                      className={clsx(
                                        i === 0 ? "mt-4" : "",
                                        `font-${data?.department.agendaFont}`,
                                        "ml-2 xl:ml-4 mb-2 text-black",
                                      )}
                                    >
                                      <div className="flex">
                                        <p
                                          className={clsx(
                                            `font-${data?.department.agendaFont}`,
                                            topic.hasDetails
                                              ? "cursor-pointer hover:underline hover:text-blue-500 text-blue-700"
                                              : "text-black",
                                            data?.department.agendaOptions
                                              .titleBold && "font-semibold",
                                            data?.department.agendaOptions
                                              .titleUnderline &&
                                              "underline underline-offset-2",
                                            data?.department.agendaOptions
                                              ?.titleFontSize &&
                                              `text-${data.department.agendaOptions.titleFontSize}`,
                                            "transform whitespace-pre-line",
                                          )}
                                          onClick={() => {
                                            if (topic.hasDetails) {
                                              handleChosenTopic(topic);
                                            }
                                          }}
                                        >
                                          {topic.voteNumber && (
                                            <span
                                              className={clsx(
                                                client === "tarrantcountytx"
                                                  ? "font-bold"
                                                  : "font-medium",
                                                "italic",
                                              )}
                                            >
                                              &#40;{topic.voteNumber}&#41;{" "}
                                            </span>
                                          )}
                                          {getGroupForTitle(
                                            data?.groupNumbering,
                                            index,
                                            data?.groupOrder,
                                            data?.childNumbering,
                                          )}
                                          {getItemNumber(
                                            data.actionTopics,
                                            data.itemNumbering,
                                            topic,
                                            tIdx,
                                            data?.groupNumbering,
                                            data?.childNumbering,
                                          )}
                                          {!topic.hideTimeStamp && (
                                            <span>- {topic.startTime}</span>
                                          )}
                                        </p>
                                        {data.publishStatus !== "published" &&
                                          cablecast_embed && (
                                            <AiOutlinePlayCircle
                                              className="ml-2 h-5 w-5 text-blue-600 hover:text-blue-400 transform duration-200 cursor-pointer"
                                              onClick={() =>
                                                handleVideoJump(
                                                  topic.elapsedSec,
                                                )
                                              }
                                            />
                                          )}
                                      </div>
                                      {topic.votedByBoard && (
                                        <p
                                          className={clsx(
                                            data?.department.agendaOptions
                                              ?.detailsFontSize &&
                                              `text-${data.department.agendaOptions.detailsFontSize}`,
                                            "whitespace-pre-line ml-2",
                                          )}
                                        >
                                          &#40;{topic.votedByBoard?.name}&#41;
                                        </p>
                                      )}
                                      {!topic.hideDescription &&
                                        topic.topicBlurb && (
                                          <span
                                            className={clsx(
                                              data?.department.agendaOptions
                                                ?.detailsFontSize &&
                                                `text-${data.department.agendaOptions.detailsFontSize}`,
                                              "whitespace-pre-line inline-block pl-4",
                                            )}
                                          >
                                            {topic.topicBlurb}
                                          </span>
                                        )}
                                      {!topic.hideSubItems &&
                                        topic.subTopics?.length > 0 &&
                                        topic.subTopics.map((sub, sIdx) => {
                                          if (client === "tarrantcountytx") {
                                            const letter = String.fromCharCode(
                                              97 + sIdx,
                                            ); // a, b, c, ...
                                            return (
                                              <p
                                                key={`sub-${sIdx}`}
                                                className={clsx(
                                                  data?.department.agendaOptions
                                                    ?.detailsFontSize &&
                                                    `text-${data.department.agendaOptions.detailsFontSize}`,
                                                  "ml-8 py-0.5",
                                                )}
                                              >
                                                {letter}&#41; {sub}
                                              </p>
                                            );
                                          } else {
                                            return (
                                              <p
                                                key={`sub-${sIdx}`}
                                                className={clsx(
                                                  data?.department.agendaOptions
                                                    ?.detailsFontSize &&
                                                    `text-${data.department.agendaOptions.detailsFontSize}`,
                                                  "ml-8 py-0.5",
                                                )}
                                              >
                                                &#8212; {sub}
                                              </p>
                                            );
                                          }
                                        })}
                                      {topic.publicHearing.openTime !==
                                        null && (
                                        <p
                                          className={clsx(
                                            data?.department.agendaOptions
                                              ?.detailsFontSize &&
                                              `text-${data.department.agendaOptions.detailsFontSize}`,
                                            "whitespace-pre-line ml-2",
                                          )}
                                        >
                                          Public Hearing was opened at{" "}
                                          {topic.publicHearing.openTime}
                                          {topic.publicHearing.closeTime !==
                                            null &&
                                            ` and closed at ${topic.publicHearing.closeTime}`}
                                        </p>
                                      )}
                                      {topic.removedFromConsent && (
                                        <div className="mt-1">
                                          <p
                                            className={clsx(
                                              data?.department.agendaOptions
                                                ?.detailsFontSize &&
                                                `text-${data.department.agendaOptions.detailsFontSize}`,
                                              "whitespace-pre-line ml-2 text-red-600",
                                            )}
                                          >
                                            &#x2a;&#x2a; Item was Removed from
                                            Consent
                                          </p>
                                        </div>
                                      )}
                                      {topic.minutesInfo.length > 0 &&
                                        topic.minutesEditor === 1 && (
                                          <EditorHTML
                                            value={topic.minutesInfo}
                                          />
                                        )}
                                      {topic.minutesInfo.length > 0 &&
                                        topic.minutesEditor === 2 && (
                                          <div
                                            className={`mb-4 ${styles.tinyContainer}`}
                                          >
                                            {textToHtmlTinyMCE(
                                              topic.minutesInfo,
                                            )}
                                          </div>
                                        )}
                                      {!bulkVote &&
                                        !topic.agendaGroup?.parentGroup
                                          ?.bulkVote &&
                                        topic.motions?.length > 0 && (
                                          <div className="mt-1">
                                            {topic.motions.map(
                                              (motion, mIdx) => {
                                                let insertedText = "";
                                                if (motion.overWriteDefault) {
                                                  insertedText = motion.wording;
                                                } else {
                                                  insertedText =
                                                    generateMotionWording(
                                                      data.board.motionText,
                                                      motion,
                                                      topic,
                                                    );
                                                }
                                                return (
                                                  <div
                                                    // className="text-sm lg:text-base mt-1 font-medium"
                                                    className={clsx(
                                                      `font-${data?.department.agendaFont}`,
                                                      data?.department
                                                        .agendaOptions
                                                        .motionBold &&
                                                        "font-semibold",
                                                      data?.department
                                                        .agendaOptions
                                                        ?.motionFontSize &&
                                                        `text-${data.department.agendaOptions.motionFontSize}`,
                                                    )}
                                                    key={`group-${index}-motion2-${mIdx}`}
                                                  >
                                                    <p>{insertedText}</p>
                                                    {!motion.overWriteDefault &&
                                                      motion.wording?.length >
                                                        0 && (
                                                        <p>{motion.wording}</p>
                                                      )}
                                                  </div>
                                                );
                                              },
                                            )}
                                          </div>
                                        )}
                                      {(bulkVote ||
                                        topic.agendaGroup?.parentGroup
                                          ?.bulkVote) &&
                                        (topic.removedFromConsent ||
                                          client === "tarrantcountytx") &&
                                        topic.motions?.length > 0 && (
                                          <div className="mt-1">
                                            {topic.motions.map(
                                              (motion, mIdx) => {
                                                let insertedText = "";
                                                if (motion.overWriteDefault) {
                                                  insertedText = motion.wording;
                                                } else {
                                                  insertedText =
                                                    generateMotionWording(
                                                      data.board.motionText,
                                                      motion,
                                                      topic,
                                                    );
                                                }
                                                return (
                                                  <div
                                                    // className="text-sm lg:text-base mt-1 font-medium"
                                                    className={clsx(
                                                      `font-${data?.department.agendaFont}`,
                                                      data?.department
                                                        .agendaOptions
                                                        .motionBold &&
                                                        "font-semibold",
                                                      data?.department
                                                        .agendaOptions
                                                        ?.motionFontSize &&
                                                        `text-${data.department.agendaOptions.motionFontSize}`,
                                                    )}
                                                    key={`group-${index}-motion3-${mIdx}`}
                                                  >
                                                    <p>{insertedText}</p>
                                                    {!motion.overWriteDefault &&
                                                      motion.wording?.length >
                                                        0 && (
                                                        <p>{motion.wording}</p>
                                                      )}
                                                  </div>
                                                );
                                              },
                                            )}
                                          </div>
                                        )}
                                    </div>
                                  </Tippy>
                                </div>
                              );
                            }
                          })}
                        </div>
                      </>
                    );
                  })}
              </div>
            </div>
            {/* Footer Blurbs */}
            {/* <div className="mt-4">
              {data.publishStatus === "published" ||
              data.publishStatus === "completed" ? (
                <>{textToHtml(data?.pdfAgenda.agendaFooterBlurb)}</>
              ) : (
                // <p className="text-sm lg:text-base 2xl:text-lg text-gray-900 font-medium">
                //   {data?.pdfAgenda.AgendaFooterBlurb}
                // </p>
                <>{textToHtml(data?.pdfMinutes.motionFooterBlurb)}</>
                // <p className="text-sm lg:text-base 2xl:text-lg text-gray-900 font-medium">
                //   {data?.pdfMinutes.motionFooterBlurb}
                // </p>
              )}
            </div> */}
            {platform && (
              <div className="mt-6 flex w-full justify-between">
                <div>{textToHtml(data?.pdfMinutes.motionFooterNote)}</div>
              </div>
            )}
          </div>

          {/* Mobile Data */}
          <div className="px-2 sm:px-4 lg:hidden">
            <div className="w-full flex flex-col space-y-2">
              {groupOrder &&
                groupOrder.map((group, index) => {
                  const { bulkVote } = group;
                  const minutesInfo = getMinutesInfoByGroupId(
                    group._id,
                    data.groupSubheadings,
                  );
                  const name = getNameByGroupId(
                    group._id,
                    data.groupSubheadings,
                    group.name,
                  );
                  const groupActions = data.actionTopics
                    .map((topic) => {
                      if (
                        topic.agendaGroup._id.toString() ===
                        group._id.toString()
                      ) {
                        const motionWithVoteNumber = topic.motions?.find(
                          (motion) => motion.useVoteNumber === true,
                        );

                        let voteNumber = motionWithVoteNumber?.voteNumber ?? "";

                        if (client === "tarrantcountytx" && voteNumber) {
                          voteNumber = `Court Order Number ${voteNumber.substring(
                            1,
                          )}`;
                        }

                        return {
                          ...topic,
                          voteNumber,
                        };
                      }
                      return null;
                    })
                    .filter((topic) => topic !== null);
                  groupActions.sort((a, b) => {
                    if (a.bulkTopic === true) return -1; // Move the entry with bulkTopic: true to the front
                    if (b.bulkTopic === true) return 1; // Move the entry with bulkTopic: true to the front
                    return a.order - b.order; // Sort by the order property for other entries
                  });
                  return (
                    <div key={index}>
                      <div
                        className={clsx(
                          `font-${data?.department.agendaFont}`,
                          data?.coverOptions?.useSeperators &&
                            "border-b border-black",
                          "mb-3 mt-2",
                        )}
                      >
                        <p className="text-black text-mobile sm:text-sm font-semibold">
                          {index + 1}. {name}
                        </p>
                      </div>
                      {minutesInfo?.length > 0 &&
                        isStringifiedObject(minutesInfo) && (
                          <EditorHTMLSmall value={minutesInfo} />
                        )}
                      {minutesInfo.length > 0 &&
                        !isStringifiedObject(minutesInfo) && (
                          <div className={`mb-4 ${styles.tinyContainer}`}>
                            {textToHtmlTinyMCE(minutesInfo)}
                          </div>
                        )}
                      {groupActions.map((topic, i) => {
                        if (topic.bulkTopic) {
                          return (
                            <div className="mt-1 mb-3" key={i}>
                              {topic.minutesInfo.length > 0 &&
                                topic.minutesEditor === 1 && (
                                  <EditorHTMLSmall value={topic.minutesInfo} />
                                )}
                              {topic.minutesInfo.length > 0 &&
                                topic.minutesEditor === 2 && (
                                  <div
                                    className={`mb-4 ${styles.tinyContainer}`}
                                  >
                                    {textToHtmlTinyMCE(topic.minutesInfo)}
                                  </div>
                                )}
                              {topic.motions.map((motion, mIdx) => {
                                let insertedText = "";
                                if (motion.overWriteDefault) {
                                  insertedText = motion.wording;
                                } else {
                                  insertedText = generateMotionWording(
                                    data.board.motionText,
                                    motion,
                                    topic,
                                  );
                                }

                                return (
                                  <div
                                    className={clsx(
                                      data?.department.agendaFont === "arial" &&
                                        "font-arial",
                                      data?.department.agendaFont ===
                                        "sans-serif" && "font-sans",
                                      data?.department.agendaFont ===
                                        "timesNewRoman" && "font-timesNewRoman",
                                      data?.department.agendaFont === "noto" &&
                                        "font-noto",
                                      "text-sm lg:text-base mt-1 font-medium whitespace-pre-line",
                                    )}
                                    key={`group-${index}-motion-${mIdx}`}
                                  >
                                    <p className="">{insertedText}</p>
                                    {!motion.overWriteDefault &&
                                      motion.wording?.length > 0 && (
                                        <p className="italic">
                                          {motion.wording}
                                        </p>
                                      )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        } else {
                          let tIdx = i;
                          if (bulkVote) {
                            tIdx--;
                          }
                          return (
                            <div
                              key={topic.id}
                              className={clsx(
                                i === 0 ? "mt-4" : "",
                                data?.department.agendaFont === "arial" &&
                                  "font-arial",
                                data?.department.agendaFont === "sans-serif" &&
                                  "font-sans",
                                data?.department.agendaFont ===
                                  "timesNewRoman" && "font-timesNewRoman",
                                data?.department.agendaFont === "noto" &&
                                  "font-noto",
                                "mb-2 select-none transition duration-200 cursor-pointer",
                              )}
                            >
                              <div
                                className={clsx(
                                  data?.department.agendaFont === "arial" &&
                                    "font-arial",
                                  data?.department.agendaFont ===
                                    "sans-serif" && "font-sans",
                                  data?.department.agendaFont ===
                                    "timesNewRoman" && "font-timesNewRoman",
                                  data?.department.agendaFont === "noto" &&
                                    "font-noto",
                                  "flex",
                                )}
                              >
                                <p
                                  className={clsx(
                                    topic.hasDetails
                                      ? "text-blue-700"
                                      : "text-black",
                                    "ml-0.5 sm:ml-2 text-mobile sm:text-sm font-bold whitespace-pre-line",
                                  )}
                                  onClick={() => {
                                    if (topic.hasDetails) {
                                      handleMobileTopic(topic);
                                    }
                                  }}
                                >
                                  {topic.voteNumber && (
                                    <span
                                      className={clsx(
                                        client === "tarrantcountytx"
                                          ? "font-bold"
                                          : "font-medium",
                                        "italic",
                                      )}
                                    >
                                      &#40;{topic.voteNumber}&#41;{" "}
                                    </span>
                                  )}
                                  {getGroupForTitle(
                                    data?.groupNumbering,
                                    index,
                                    data?.groupOrder,
                                    data?.childNumbering,
                                  )}
                                  {getItemNumber(
                                    data.actionTopics,
                                    data.itemNumbering,
                                    topic,
                                    tIdx,
                                    data?.groupNumbering,
                                    data?.childNumbering,
                                  )}
                                  {!topic.hideTimeStamp && (
                                    <span className="font-normal text-xs lg:text-sm text-black">
                                      - {topic.startTime}
                                    </span>
                                  )}
                                </p>
                                {data.publishStatus !== "published" &&
                                  cablecast_embed && (
                                    <AiOutlinePlayCircle
                                      className="ml-2 h-5 w-5 text-blue-600 hover:text-blue-400 transform duration-200 cursor-pointer"
                                      onClick={() =>
                                        handleVideoJump(topic.elapsedSec)
                                      }
                                    />
                                  )}
                              </div>
                              {topic.votedByBoar && (
                                <p className="italic font-medium">
                                  &#40;{topic.votedByBoard?.name}&#41;
                                </p>
                              )}
                              {!topic.hideDescription && topic.topicBlurb && (
                                <p className="ml-2 text-mobile whitespace-pre-line">
                                  {topic.topicBlurb}
                                </p>
                              )}

                              {!topic.hideSubItems &&
                                topic.subTopics?.length > 0 &&
                                topic.subTopics.map((sub, sIdx) => {
                                  if (client === "tarrantcountytx") {
                                    const letter = String.fromCharCode(
                                      97 + sIdx,
                                    ); // a, b, c, ...
                                    return (
                                      <p
                                        key={`sub-${sIdx}`}
                                        className="py-0.5 ml-2 text-mobile"
                                      >
                                        {letter}&#41; {sub}
                                      </p>
                                    );
                                  } else {
                                    return (
                                      <p
                                        key={`sub-${sIdx}`}
                                        className="py-0.5 ml-2 text-mobile"
                                      >
                                        &#8212; {sub}
                                      </p>
                                    );
                                  }
                                })}
                              {topic.publicHearing.openTime !== null && (
                                <p className="text-sm lg:text-base">
                                  Public Hearing was opened at{" "}
                                  {topic.publicHearing.openTime}
                                  {topic.publicHearing.closeTime !== null &&
                                    ` and closed at ${topic.publicHearing.closeTime}`}
                                </p>
                              )}
                              {bulkVote && topic.removedFromConsent && (
                                <div className="mt-1">
                                  <p className="font-semibold text-sm lg:text-base text-red-600">
                                    &#x2a;&#x2a; Item was Removed from Consent
                                  </p>
                                </div>
                              )}
                              {topic.minutesInfo.length > 0 &&
                                topic.minutesEditor === 1 && (
                                  <EditorHTMLSmall value={topic.minutesInfo} />
                                )}
                              {topic.minutesInfo.length > 0 &&
                                topic.minutesEditor === 2 && (
                                  <div
                                    className={`mb-4 ${styles.tinyContainer}`}
                                  >
                                    {textToHtmlTinyMCE(topic.minutesInfo)}
                                  </div>
                                )}
                              {!bulkVote && topic.motions?.length > 0 && (
                                <div className="mt-1">
                                  {topic.motions.map((motion, mIdx) => {
                                    let insertedText = "";
                                    if (motion.overWriteDefault) {
                                      insertedText = motion.wording;
                                    } else {
                                      insertedText = generateMotionWording(
                                        data.board.motionText,
                                        motion,
                                        topic,
                                      );
                                    }
                                    return (
                                      <div
                                        className="text-sm lg:text-base mt-1 font-medium whitespace-pre-line"
                                        key={`group-${index}-motion2-${mIdx}`}
                                      >
                                        <p className="">{insertedText}</p>
                                        {!motion.overWriteDefault &&
                                          motion.wording?.length > 0 && (
                                            <p className="italic">
                                              {motion.wording}
                                            </p>
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {bulkVote &&
                                topic.removedFromConsent &&
                                topic.motions?.length > 0 && (
                                  <div className="mt-1">
                                    {topic.motions.map((motion, mIdx) => {
                                      let insertedText = "";
                                      if (motion.overWriteDefault) {
                                        insertedText = motion.wording;
                                      } else {
                                        insertedText = generateMotionWording(
                                          data.board.motionText,
                                          motion,
                                          topic,
                                        );
                                      }
                                      return (
                                        <div
                                          className="text-sm lg:text-base mt-1 font-medium whitespace-pre-line"
                                          key={`group-${index}-motion3-${mIdx}`}
                                        >
                                          {/* <p className="font-semibold text-red-600">
                                              &#x2a;&#x2a; Item was Removed from
                                              Consent
                                            </p> */}
                                          <p className="">{insertedText}</p>
                                          {!motion.overWriteDefault &&
                                            motion.wording?.length > 0 && (
                                              <p className="italic">
                                                {motion.wording}
                                              </p>
                                            )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                })}
              <div className="mt-4"></div>
              {platform && (
                <div className="mt-6 flex w-full justify-between">
                  <div>{textToHtml(data?.pdfMinutes.motionFooterNote)}</div>
                </div>
              )}
              {/* {platform?.useSeal && (
                <div>
                  <img
                    src={platform.signatureSeal}
                    alt="signature"
                    className="h-16 w-16 rounded-full"
                  />
                </div>
              )} */}
            </div>
          </div>
          {/* End Content */}
        </div>
      )}
      <AnimatePresence>
        {showDetails && (
          <AgendaTopicFull
            visible={setShowDetails}
            topic={topicDetails.id}
            enablePri={platform?.enablePriorities}
            client={client}
          />
        )}
      </AnimatePresence>
      {supplemental?.visible && (
        <Supplemental
          meeting={supplemental?.meeting}
          update={setSupplemental}
        />
      )}

      <AnimatePresence>
        {mobileDetails && (
          <MinutesTopicInfoMobile
            show={mobileDetails}
            visible={setMobileDetails}
            topic={topicDetails.id}
            enablePri={platform?.enablePriorities}
            client={client}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {mobileSupplemental?.visible && (
          <SupplementalMobile
            meeting={mobileSupplemental?.meeting}
            update={setMobileSupplemental}
          />
        )}
      </AnimatePresence>
      {loading && <Loading />}
    </div>
  );
};

export default Minutes;
